import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import Fade from '@mui/material/Fade';
import cx from 'classnames';

import { LLPreloader, LLProgressBar, LLTextSlider } from '../../../../components';
import Markets from '../../components/common/markets';
import trans from '../../../../common/utils/trans';
import CustomBetsBuilder from '../../components/custom-bets/custom-bets-builder';
import SubMarketGroupsSlider from './sub-market-group-slider';
import { FavMarketsList } from '../index';
import SearchMarketGroup from './search-market-group';

const MarketGroupsSlider = ({
    store,
    activeMarketGroup,
    setActiveMarketGroup,
    matchInstance,
    isMarketGroupsNeeded,
    userCanCashout,
    type = 'live',
}) => {
    const activeItems = store.betting.activeItems,
        isCustomBetsView = store.betting.isCustomBetsView,
        isBreadcrumbsNavigationDropdownOpen = store.betting.isBreadcrumbsNavigationDropdownOpen,
        isOldView = store.user.isOldMarketsView,
        setOldView = store.user.setMarketViewType,
        marketGroups = matchInstance?.marketGroupsWithOutParentList() ?? [],
        marketGroupsForAllMarkets = matchInstance?.marketGroupsList() ?? [],
        marketGroupsListSearchedBy = matchInstance?.marketGroupsListSearchedBy,
        marketGroup = marketGroups[activeMarketGroup];

    const [activeSubMarketGroup, setActiveSubMarketGroup] = useState(null);
    const [searchIsOpened, setSearchOpened] = useState(false);
    const [searchMarketValue, setSearchMarketValue] = useState('');

    useEffect(() => {
        if (activeMarketGroup === -777 && !store.user.isLogged) {
            setActiveMarketGroup(-1);
        }
    }, [store.user.isLogged, activeMarketGroup]);

    useEffect(() => {
        if (store.user.isLogged) {
            store.betting.favorites.getFavoriteMarkets();
        }
    }, [store.user.isLogged]);

    const renderMarketGroupsTabs = (marketGroups) => (
        <div
            className={cx(
                `${type}-market-groups__slider`,
                'market-groups__slider',
                !!marketGroups.find(
                    (group) =>
                        group.initialFetching &&
                        activeItems.isActive({
                            id: group.id,
                            type: 'marketGroups',
                        })
                )
                    ? 'market-groups__slider--disabled'
                    : ''
            )}
        >
            <SearchMarketGroup
                searchMarketValue={searchMarketValue}
                setSearchMarketValue={setSearchMarketValue}
                isOpened={searchIsOpened}
                setOpened={setSearchOpened}
            />

            {/* FAVORITE MARKETS */}
            {store.user.isLogged ? (
                <div className="pre-match-details__market-group__favorites-wrp">
                    <div
                        className={`pre-match-details__market-group__favorites ${type}-match-details__market-group${
                            activeMarketGroup === -777 ? '--active' : ''
                        }`}
                        onClick={() => {
                            setActiveMarketGroup(-777);
                            setActiveSubMarketGroup(null);
                            activeItems.setActiveItem(['-777'], 'marketGroups');
                        }}
                        id={-777}
                    >
                        <span className="pre-match-details__market-group__fav-label">
                            <i className="pre-match-details__market-group__fav-icon doruk-star-fill"></i>
                        </span>
                    </div>
                    {matchInstance?.favoriteMarketsLength > 0 && !searchIsOpened ? (
                        <div className="pre-match-details__market-group__favorites-label">
                            {matchInstance?.favoriteMarketsLength}
                        </div>
                    ) : null}
                    {store.betting.favorites.marketsLoading ? (
                        <div className="pre-match-details__market-group__fav-loading">
                            <LLPreloader visible={store.betting.favorites.marketsLoading} width={20} height={20} />
                        </div>
                    ) : null}
                </div>
            ) : null}
            {/* END OF FAVORITE MARKETS */}
            <LLTextSlider
                id={`${type}-market-groups__slider`}
                viewSize={store.site.status.viewSize}
                customClasses={{
                    arrow: 'market-groups__slider-arrow',
                }}
                activeItem={activeMarketGroup}
            >
                {/* CUSTOMBETS */}
                {matchInstance.hasCustom ? (
                    <div
                        className={`pre-match-details__market-group__custom-bets ${type}-match-details__market-group${
                            activeMarketGroup === -999 ? '--active' : ''
                        }`}
                        onClick={() => {
                            setActiveMarketGroup(-999);
                            setActiveSubMarketGroup(null);
                            activeItems.setActiveItem(['-999'], 'marketGroups');
                        }}
                        id={-999}
                    >
                        {trans('Bet Builder')}
                    </div>
                ) : null}
                {/* END OF CUSTOMBETS */}

                <div
                    className={`${type}-match-details__market-group${activeMarketGroup === -1 ? '--active' : ''}`}
                    onClick={() => {
                        setActiveMarketGroup(-1);
                        setActiveSubMarketGroup(null);
                        activeItems.setActiveItem(['-1'], 'marketGroups');
                        matchInstance.initializeAllMarketsInstruction(true);
                    }}
                    id={-1}
                >
                    {trans('All Markets')}
                </div>

                {isMarketGroupsNeeded &&
                    marketGroups.map((marketGroup, i) => {
                        return (
                            <div
                                className={`${type}-match-details__market-group${
                                    activeMarketGroup === marketGroup.id && !marketGroup.initialFetching
                                        ? '--active'
                                        : ''
                                }`}
                                onClick={() => {
                                    setActiveMarketGroup(marketGroup.id);
                                    const marketGroupChildrenList = matchInstance.marketGroupChildrenList(
                                        marketGroup.id
                                    );
                                    if (type !== 'live' && marketGroupChildrenList.length) {
                                        setActiveSubMarketGroup(marketGroupChildrenList[0].id);
                                        activeItems.setActiveItem(
                                            [marketGroupChildrenList[0].id.toString()],
                                            'marketGroups'
                                        );
                                    } else {
                                        setActiveSubMarketGroup(null);
                                        activeItems.setActiveItem([marketGroup.id.toString()], 'marketGroups');
                                    }
                                }}
                                id={marketGroup.id}
                                key={i}
                            >
                                {marketGroup.name}
                                {marketGroup.initialFetching &&
                                    activeItems.isActive({
                                        id: marketGroup.id,
                                        type: 'marketGroups',
                                    }) && <LLProgressBar customClasses={`${type}-match-details__progress-bar`} />}
                            </div>
                        );
                    })}
            </LLTextSlider>
        </div>
    );

    const renderMarketsWithoutGroups = () => {
        const mGroups = searchMarketValue?.length
            ? marketGroupsListSearchedBy(searchMarketValue)
            : marketGroupsForAllMarkets;

        if (searchMarketValue?.length && !mGroups.length) {
            return <div className="match-detail__no-search-markets">{trans(trans('NO_SEARCH_MARKETS_TEXT'))}</div>;
        }

        let marketsCount = 0;
        return mGroups.map((marketGroup) => {
            marketsCount = marketsCount + marketGroup.marketsListLength();
            return (
                <div className="match-details__market-group" data-market-group-id="all-markets" key={marketGroup.id}>
                    <Markets
                        match={matchInstance}
                        marketGroup={marketGroup}
                        userCanCashout={userCanCashout}
                        viewSize={store.site.status.viewSize}
                        marketsCount={marketsCount}
                        isOldView={isOldView}
                        setOldView={setOldView}
                        activeMarketGroup={activeMarketGroup}
                        user={store.user}
                        site={store.site}
                        favorites={store.betting.favorites}
                        searchMarketValue={searchMarketValue}
                    />
                </div>
            );
        });
    };

    if (!!marketGroups.length && !!matchInstance.oddsCount) {
        return (
            <>
                {matchInstance?.initialFetching ? (
                    <LLProgressBar customClasses={`${type}-match-details__progress-bar`} />
                ) : (
                    <div className="match-detail__wrapper">
                        {isBreadcrumbsNavigationDropdownOpen && store.site.status.viewSize === 'mobile' ? (
                            <div
                                className="match-detail__back-drop"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                }}
                            />
                        ) : null}
                        {renderMarketGroupsTabs(marketGroups)}
                        <Fade
                            in={
                                isCustomBetsView ||
                                !marketGroup?.initialFetching ||
                                (activeMarketGroup === -1 && !matchInstance.isFetching)
                            }
                        >
                            <div>
                                {searchMarketValue ? (
                                    !matchInstance.isFetching ? (
                                        renderMarketsWithoutGroups()
                                    ) : (
                                        <div className="match-details__all-markets-loading">
                                            <LLProgressBar customClasses="match-details__all-markets-progress-bar" />
                                        </div>
                                    )
                                ) : activeMarketGroup === -777 ? (
                                    <FavMarketsList
                                        matchInstance={matchInstance}
                                        activeMarketGroup={activeMarketGroup}
                                    />
                                ) : activeMarketGroup === -999 ? (
                                    <CustomBetsBuilder matchInstance={matchInstance} />
                                ) : activeMarketGroup === -1 ? (
                                    isCustomBetsView || !matchInstance.isFetching ? (
                                        renderMarketsWithoutGroups()
                                    ) : (
                                        <div className="match-details__all-markets-loading">
                                            <LLProgressBar customClasses="match-details__all-markets-progress-bar" />
                                        </div>
                                    )
                                ) : activeSubMarketGroup ? (
                                    <SubMarketGroupsSlider
                                        activeMarketGroup={activeSubMarketGroup}
                                        setActiveMarketGroup={setActiveSubMarketGroup}
                                        marketGroups={matchInstance.marketGroupChildrenList(activeMarketGroup)}
                                        userCanCashout={userCanCashout}
                                        matchInstance={matchInstance}
                                    />
                                ) : (
                                    <Markets
                                        match={matchInstance}
                                        marketGroup={matchInstance.marketGroups.get(activeMarketGroup)}
                                        sportId={matchInstance.sportId}
                                        userCanCashout={userCanCashout}
                                        viewSize={store.site.status.viewSize}
                                        isOldView={isOldView}
                                        setOldView={setOldView}
                                        activeMarketGroup={activeMarketGroup}
                                        user={store.user}
                                        site={store.site}
                                        favorites={store.betting.favorites}
                                    />
                                )}
                            </div>
                        </Fade>
                    </div>
                )}
            </>
        );
    } else {
        return <div className="match-details__empty-markets">{type === 'pre' ? trans('EMPTY_BETS_ERROR') : null}</div>;
    }
};
export default inject('store')(observer(MarketGroupsSlider));
